import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ActiveDealRow } from '@metycle/shared';

import { getApiBaseUrl } from '@/lib/api-base-url';
import { authFetch } from '@/lib/auth-fetch';

export const ACTIVE_DEALS_QUERY_KEY = ['financing', 'active-deals'] as const;

export interface BlLevelRowPatchResponse {
  id: string;
  updated_at: string;
}

export class BlLevelRowPatchError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'BlLevelRowPatchError';
    this.status = status;
  }
}

export interface BlLevelRowPatchContext<TRow> {
  previousRows: TRow[] | undefined;
}

async function readError(response: Response): Promise<BlLevelRowPatchError> {
  let message = `Request failed (${String(response.status)})`;
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === 'string') message = body.message;
  } catch {
    // fall through to default message
  }
  return new BlLevelRowPatchError(response.status, message);
}

async function runPatch(
  blOrderId: string,
  body: Record<string, unknown>,
): Promise<BlLevelRowPatchResponse> {
  const response = await authFetch(`${getApiBaseUrl()}/bl-level/${blOrderId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as BlLevelRowPatchResponse;
}

export interface BlLevelRowPatchTarget<TRow extends { updated_at: string }> {
  readonly queryKey: readonly unknown[];
  readonly getRowId: (row: TRow) => string;
  readonly setUpdatedAt: (row: TRow, updatedAt: string) => TRow;
  readonly notLoadedMessage: string;
}

export interface BlLevelRowPatchOptions<TRow, TVars extends { blOrderId: string }> {
  toBody: (variables: TVars, cached: TRow) => Record<string, unknown>;
  applyOptimistic: (row: TRow, variables: TVars) => TRow;
}

const BL_LEVEL_INVALIDATE_KEY = ['bl-level'] as const;

function mapMatchingRow<TRow>(
  rows: TRow[],
  matches: (row: TRow) => boolean,
  transform: (row: TRow) => TRow,
): TRow[] {
  return rows.map((row) => (matches(row) ? transform(row) : row));
}

async function buildMutationFn<
  TRow extends { updated_at: string },
  TVars extends { blOrderId: string },
>(
  queryClient: ReturnType<typeof useQueryClient>,
  target: BlLevelRowPatchTarget<TRow>,
  options: BlLevelRowPatchOptions<TRow, TVars>,
  variables: TVars,
): Promise<BlLevelRowPatchResponse> {
  const cached = queryClient
    .getQueryData<TRow[]>(target.queryKey)
    ?.find((row) => target.getRowId(row) === variables.blOrderId);
  if (cached === undefined) throw new BlLevelRowPatchError(400, target.notLoadedMessage);
  return runPatch(variables.blOrderId, {
    expected_updated_at: cached.updated_at,
    ...options.toBody(variables, cached),
  });
}

function buildRowMutation<TRow extends { updated_at: string }, TVars extends { blOrderId: string }>(
  queryClient: ReturnType<typeof useQueryClient>,
  target: BlLevelRowPatchTarget<TRow>,
  options: BlLevelRowPatchOptions<TRow, TVars>,
) {
  const matchesVariables = (variables: TVars) => (row: TRow) =>
    target.getRowId(row) === variables.blOrderId;
  return {
    mutationFn: (variables: TVars) => buildMutationFn(queryClient, target, options, variables),
    onMutate: (variables: TVars): BlLevelRowPatchContext<TRow> => {
      const previousRows = queryClient.getQueryData<TRow[]>(target.queryKey);
      if (previousRows !== undefined) {
        queryClient.setQueryData<TRow[]>(
          target.queryKey,
          mapMatchingRow(previousRows, matchesVariables(variables), (row) =>
            options.applyOptimistic(row, variables),
          ),
        );
      }
      return { previousRows };
    },
    onError: (
      _err: BlLevelRowPatchError,
      _variables: TVars,
      context: BlLevelRowPatchContext<TRow> | undefined,
    ) => {
      if (context?.previousRows !== undefined) {
        queryClient.setQueryData<TRow[]>(target.queryKey, context.previousRows);
      }
    },
    onSuccess: (response: BlLevelRowPatchResponse, variables: TVars) => {
      queryClient.setQueryData<TRow[]>(target.queryKey, (current) =>
        current === undefined
          ? current
          : mapMatchingRow(current, matchesVariables(variables), (row) =>
              target.setUpdatedAt(row, response.updated_at),
            ),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: BL_LEVEL_INVALIDATE_KEY });
    },
  };
}

export function useBlLevelRowMutation<
  TRow extends { updated_at: string },
  TVars extends { blOrderId: string },
>(target: BlLevelRowPatchTarget<TRow>, options: BlLevelRowPatchOptions<TRow, TVars>) {
  const queryClient = useQueryClient();
  return useMutation<
    BlLevelRowPatchResponse,
    BlLevelRowPatchError,
    TVars,
    BlLevelRowPatchContext<TRow>
  >(buildRowMutation(queryClient, target, options));
}

const ACTIVE_DEALS_TARGET: BlLevelRowPatchTarget<ActiveDealRow> = {
  queryKey: ACTIVE_DEALS_QUERY_KEY,
  getRowId: (row) => row.id,
  setUpdatedAt: (row, updatedAt) => ({ ...row, updated_at: updatedAt }),
  notLoadedMessage: 'BL Order not loaded in the active-deals table — refresh and try again.',
};

export function useBlLevelRowPatch<TVars extends { blOrderId: string }>(
  options: BlLevelRowPatchOptions<ActiveDealRow, TVars>,
) {
  return useBlLevelRowMutation(ACTIVE_DEALS_TARGET, options);
}
